import type { ApiClient } from "../api/client";
import type { Saved } from "../api/types";
import { createStore } from "./store";

/**
 * どれを保存しているかの目印（お気に入りの歌唱・保存した投稿）。
 * 契約の歌唱・投稿には「保存しているか」が無く、保存の取り消しは保存の ID で行う（DELETE /library/{id}）。
 * そのため保存の一覧（GET /library）を読み、対象 → 保存の ID を引けるようにしておく。
 * 表示はサーバーの応答が返ってから変える（押した瞬間に保存済みにしない）
 */
export interface SavedState {
  status: "idle" | "loading" | "ready" | "unavailable";
  byPerformance: Map<string, string>;
  byPost: Map<string, string>;
}

export type SaveTarget = { performance_id: string; post_id?: undefined } | { post_id: string; performance_id?: undefined };

export class SavedIndex {
  readonly store = createStore<SavedState>({ status: "idle", byPerformance: new Map(), byPost: new Map() });
  private seq = 0;

  constructor(private api: ApiClient) {}

  async load(): Promise<void> {
    if (!this.api.isConnected("library")) {
      this.store.set((s) => ({ ...s, status: "unavailable" }));
      return;
    }
    const mine = ++this.seq;
    this.store.set((s) => ({ ...s, status: s.status === "ready" ? "ready" : "loading" }));
    try {
      const items = await this.api.listSaved();
      if (mine !== this.seq) return;
      this.store.set(index(items));
    } catch {
      if (mine !== this.seq) return;
      this.store.set((s) => ({ ...s, status: s.status === "ready" ? "ready" : "unavailable" }));
    }
  }

  idFor(target: SaveTarget, s: SavedState = this.store.get()): string | null {
    return (target.post_id ? s.byPost.get(target.post_id) : s.byPerformance.get(target.performance_id!)) ?? null;
  }

  /** 保存・取り消しが成功したとき */
  set(target: SaveTarget, savedId: string | null) {
    this.store.set((s) => {
      const byPerformance = new Map(s.byPerformance);
      const byPost = new Map(s.byPost);
      const map = target.post_id ? byPost : byPerformance;
      const key = (target.post_id ?? target.performance_id)!;
      if (savedId) map.set(key, savedId);
      else map.delete(key);
      return { ...s, byPerformance, byPost };
    });
  }

  clear() {
    this.seq++;
    this.store.set({ status: "idle", byPerformance: new Map(), byPost: new Map() });
  }
}

function index(items: Saved[]): SavedState {
  const byPerformance = new Map<string, string>();
  const byPost = new Map<string, string>();
  for (const s of items) {
    if (s.post) byPost.set(s.post.id, s.id);
    else byPerformance.set(s.performance.id, s.id);
  }
  return { status: "ready", byPerformance, byPost };
}
